import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { listDeletionRequests, deleteUser } from "@/lib/api/admin";
import type { DeletionRequest } from "@/lib/api/types";
import { DataTable, type Column } from "@/components/DataTable";
import { Pagination } from "@/components/Pagination";
import { PAGE_SIZE } from "@/lib/constants";
import { formatDate, formatDateTime } from "@/lib/utils/date";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Trash2 } from "lucide-react";

export function UserDeletionRequestsPage() {
  const user = useAdminAuth();
  const qc = useQueryClient();

  const [offset, setOffset] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState<DeletionRequest | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["deletion-requests", offset],
    queryFn: async () => listDeletionRequests(user!, { limit: PAGE_SIZE, offset }),
    enabled: !!user,
  });

  const deleteMutation = useMutation({
    mutationFn: async (uid: string) => deleteUser(user!, uid),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["deletion-requests"] });
      setDeleteTarget(null);
      setDeleteConfirmName("");
      toast.success("User permanently deleted.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const columns: Column<DeletionRequest>[] = [
    {
      key: "name",
      header: "Name",
      render: (r) => (
        <div>
          <p className="font-medium">
            {r.first_name} {r.last_name}
          </p>
          <p className="text-xs font-mono text-muted-foreground">{r.user_uid}</p>
        </div>
      ),
    },
    {
      key: "joined",
      header: "Joined",
      render: (r) => (
        <span className="text-sm text-muted-foreground">{formatDate(r.created_at)}</span>
      ),
    },
    {
      key: "requested",
      header: "Requested At",
      render: (r) => (
        <span className="text-sm text-muted-foreground">
          {formatDateTime(r.deletion_requested_at)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (r) => (
        <Button
          variant="destructive"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            setDeleteTarget(r);
          }}
        >
          <Trash2 className="size-3.5 mr-1" />
          Delete Permanently
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Deletion Requests</h1>
        <p className="text-sm text-muted-foreground">Users who have requested account deletion</p>
      </div>

      <DataTable
        columns={columns}
        data={data?.data ?? []}
        isLoading={isLoading}
        rowKey={(r) => r.user_uid}
        emptyMessage="No deletion requests."
      />

      {data && (
        <Pagination
          offset={offset}
          limit={PAGE_SIZE}
          total={data.total}
          onOffsetChange={setOffset}
        />
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
            setDeleteConfirmName("");
          }
        }}
        title="Permanently delete user"
        description={`This will permanently delete "${deleteTarget?.first_name} ${deleteTarget?.last_name}" and all their data. This cannot be undone.`}
        confirmLabel="Delete Permanently"
        destructive
        isPending={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.user_uid)}
        confirmInput={{
          value: deleteConfirmName,
          onChange: setDeleteConfirmName,
          placeholder: `${deleteTarget?.first_name ?? ""} ${deleteTarget?.last_name ?? ""}`,
          requiredValue: deleteTarget
            ? `${deleteTarget.first_name} ${deleteTarget.last_name}`
            : undefined,
        }}
      />
    </div>
  );
}
